import { ActorId } from "./ActorId";
import { OrderPayslip } from "./OrderPayslip";
import { OrderModel } from "./OrderModel";
import { OrderOtherDocument } from "./OrderOtherDocument";
import { OrderIdentityVerification } from "./OrderIdentityVerification";
import { ActorPrefilledData } from "./ActorPrefilledData";
import { DocumentationAreaNotification } from "./DocumentationAreaNotification";
import { SignedAcceptanceId } from "./SignedAcceptanceId";
import { OrderItem } from "./OrderItem";
import { ActorIdentity } from "src/contexts/documentationArea/domain/ActorIdentity";
import { OpenAdminBanner } from "./OpenAdminBanner";
import type { ActorActionOption } from "./ActorAction";

export class Actor {
  private readonly id: ActorId;
  private readonly actions: ActorActionOption[];
  private readonly signedAcceptanceIds: SignedAcceptanceId[];
  private readonly notifications: DocumentationAreaNotification[];
  private readonly orders: OrderItem[];
  private readonly prefilledData: ActorPrefilledData | null;
  private readonly identity: ActorIdentity | null;
  private readonly openAdminBanner?: OpenAdminBanner;

  constructor(actor: {
    id: ActorId;
    actions?: ActorActionOption[];
    signedAcceptanceIds: SignedAcceptanceId[];
    notifications: DocumentationAreaNotification[];
    orders: OrderItem[];
    prefilledData: ActorPrefilledData | null;
    identity: ActorIdentity | null;
    openAdminBanner?: OpenAdminBanner;
  }) {
    this.id = actor.id;
    this.actions = actor.actions ?? [];
    this.signedAcceptanceIds = actor.signedAcceptanceIds;
    this.notifications = actor.notifications;
    this.orders = actor.orders;
    this.identity = actor.identity;
    this.openAdminBanner = actor.openAdminBanner;
    this.prefilledData = actor.prefilledData;
  }

  public getId() {
    return this.id;
  }

  public getActions() {
    return this.actions;
  }

  public getEmail() {
    return this.identity?.getRepresentative()?.getEmail() ?? null;
  }

  public getName() {
    return this.identity?.getRepresentative()?.getName() ?? null;
  }

  public getFirstSurname() {
    return this.identity?.getRepresentative()?.getFirstSurname() ?? null;
  }

  public getSecondSurname() {
    return this.identity?.getRepresentative()?.getSecondSurname() ?? null;
  }

  /**
   * @returns the first id number given there's only one for the moment. Will be updated with future improvements.
   */
  public getIdNumber() {
    return this.identity?.getRepresentative()?.getIdNumber() ?? null;
  }

  public hasIdNumber() {
    return this.identity?.getRepresentative()?.hasIdNumber() ?? false;
  }

  public getIdNumbers() {
    return this.identity?.getRepresentative()?.getIdNumbers() ?? [];
  }

  public getRepresentedEntityIdNumber() {
    return this.identity?.getRepresentedEntity()?.getIdNumber() ?? null;
  }

  public hasRepresentedEntityIdNumber(): boolean {
    return this.identity?.getRepresentedEntity()?.hasIdNumber() ?? false;
  }

  public hasRepresentedEntityRelationType(): boolean {
    return this.identity?.getRepresentedEntity()?.hasIndividualRelationType() ?? false;
  }

  public getRepresentativeIdNumber() {
    return this.getIdNumber();
  }

  public getNotifications() {
    return this.notifications;
  }

  public getOrders() {
    return this.orders;
  }

  public getModelOrders(): OrderModel[] {
    return this.orders.filter((order) => order.isModel()).filter((order) => !order.isCancelled());
  }

  public getPayslipOrders(): OrderPayslip[] {
    const filteredOrderPayslips = this.orders.filter((order) => order.isPayslip());

    return filteredOrderPayslips
      .filter((order) => !order.isExpired())
      .filter((order) => !order.isCancelled())
      .sort((a, b) => {
        const aRequest = a.getRequest();
        const bRequest = b.getRequest();
        if (aRequest.year !== bRequest.year) {
          return bRequest.year - aRequest.year;
        }
        return parseInt(bRequest.month) - parseInt(aRequest.month);
      });
  }

  public getOtherDocumentOrders(): OrderOtherDocument[] {
    return this.orders
      .filter((order) => order.isOtherDocument())
      .filter((order) => !order.isCancelled());
  }

  public getIdentityVerificationOrders(): OrderIdentityVerification[] {
    return this.orders
      .filter((order) => order.isIdentityVerification())
      .filter((order) => !order.isCancelled());
  }

  public getOrderById(
    orderId: string,
  ): OrderPayslip | OrderModel | OrderOtherDocument | OrderIdentityVerification | null {
    const order = this.orders.find((order) => order.getId() === orderId);
    return order ?? null;
  }

  public setOrderProcessing(orderId: string): void {
    const order = this.getOrderById(orderId);
    if (order) {
      order.setToProcessing();
    }
  }

  public getSignedAcceptanceIds() {
    return this.signedAcceptanceIds;
  }

  public getPrefilledData() {
    return this.prefilledData;
  }

  public areAllModelsCompleted(): boolean {
    const modelOrders = this.getModelOrders();
    return modelOrders.every((model) =>
      model.getResponses().some((response) => response.isValid()),
    );
  }

  public isAnyModelOrderProcessing(): boolean {
    return this.orders.filter((order) => order.isModel()).some((order) => order.isProcessing());
  }

  public getIdentity() {
    return this.identity;
  }

  public getOpenAdminBanner() {
    return this.openAdminBanner;
  }

  public isAutomaticExtractionAvailable(): boolean {
    return this.getModelOrders().some((order) =>
      order.getRetrievalMethods().some((method) => method.isAutomaticExtraction()),
    );
  }
}
